import React from 'react';
import { motion } from 'framer-motion';

// Professional Argentine context photos — white-collar workers
const PHOTOS = [{
  url: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80',
  cap: 'Contador revisando inconsistencias'
}, {
  url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80',
  cap: 'Profesional de la salud'
}, {
  url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&q=80',
  cap: 'Abogado con documentación'
}];
const STATS = [{
  n: '+2.300',
  desc: 'notificaciones ARCA enviadas a monotributistas en el 1er trimestre 2026'
}, {
  n: '74%',
  desc: 'de los profesionales no tiene un sistema de control financiero activo'
}, {
  n: '$0',
  desc: 'en dólares cobran el 80% de los profesionales que podrían hacerlo legalmente'
}];
const PAINS = [{
  emoji: '📩',
  title: 'Llegó la notificación de ARCA y no sabés qué hacer',
  text: 'Tenés 30 días para responder antes de que pase a seguimiento activo. Sin el descargo correcto, podés enfrentar multas o recategorización forzosa.'
}, {
  emoji: '📉',
  title: 'Tus ahorros en pesos pierden valor cada mes',
  text: 'Con inflación acumulada >200% en los últimos años, cada peso parado en el banco es dinero que desaparece. Y no sabés cómo protegerlos legalmente.'
}, {
  emoji: '💸',
  title: 'Podrías cobrar en dólares, pero no sabés cómo',
  text: 'Colegas con las mismas habilidades cobran en USD desde plataformas globales. Vos seguís dependiendo del mercado local porque nadie te explicó el sistema.'
}, {
  emoji: '🔀',
  title: 'Mezclás finanzas del negocio con las personales',
  text: 'Al fin de mes no sabés exactamente cuánto ganaste, cuánto gastó el consultorio, ni cuánto te quedó realmente disponible para vos.'
}, {
  emoji: '📞',
  title: 'Cada consulta al contador te cuesta dinero',
  text: 'Preguntas básicas sobre ARCA, Monotributo o inversiones generan honorarios. Con las herramientas correctas podrías resolver el 80% vos mismo.'
}, {
  emoji: '⚙️',
  title: 'Trabajás más pero no ganás más en términos reales',
  text: 'Los ingresos nominales suben, pero el poder adquisitivo cae. La estructura financiera nunca cambió — y eso tiene una solución muy concreta.'
}];
export function ProblemSection() {
  return <section className="bg-navy-dark py-16 md:py-24 px-4 overflow-hidden" data-id="element-239">
      <div className="max-w-6xl mx-auto" data-id="element-240">

        {/* ── HEADER ── */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12 md:mb-16" data-id="element-241">
          <p className="text-xs font-bold text-teal tracking-widest uppercase mb-4" data-id="element-242">
            Médico · Abogado · Contador · Arquitecto · Profesional independiente
          </p>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-white leading-[1.1] max-w-3xl mx-auto" data-id="element-243">
            ¿Te identificás con{' '}
            <span className="gold-shine" data-id="element-244">alguna</span>{' '}
            de estas situaciones?
          </h2>
          <p className="text-white/45 text-base md:text-lg mt-4 max-w-xl mx-auto" data-id="element-245">
            No sos el único. Esto es lo que vive el 78% de los profesionales independientes argentinos hoy.
          </p>
        </motion.div>

        {/* ── PHOTO GRID + STATS ── */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.65
      }} className="mb-14" data-id="element-246">
          <div className="grid grid-cols-3 gap-3 mb-5" data-id="element-247">
            {PHOTOS.map((p, i) => <div key={i} className="relative rounded-xl overflow-hidden aspect-square" data-id="element-248">
                <img src={p.url} alt={p.cap} className="w-full h-full object-cover" data-id="element-249" />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/80 via-navy-dark/20 to-transparent" data-id="element-250" />
                <p className="absolute bottom-2 left-2 right-2 text-[10px] text-white/50 leading-tight hidden md:block" data-id="element-251">
                  {p.cap}
                </p>
              </div>)}
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-252">
            {STATS.map(({
            n,
            desc
          }, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.1
          }} className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 flex items-center gap-4" data-id="element-253">
                <span className="font-display text-2xl md:text-3xl text-gold flex-shrink-0 leading-none" data-id="element-254">
                  {n}
                </span>
                <p className="text-white/50 text-xs leading-relaxed" data-id="element-255">{desc}</p>
              </motion.div>)}
          </div>
        </motion.div>

        {/* ── PAIN POINTS GRID ── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-256">
          {PAINS.map((pain, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.07
        }} className="group bg-white/[0.025] hover:bg-white/[0.045] border border-white/[0.06] hover:border-gold/25 rounded-2xl p-5 transition-all duration-300" data-id="element-257">
              {/* Emoji icon */}
              <div className="w-10 h-10 rounded-xl bg-navy-mid flex items-center justify-center text-xl mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-258">
                {pain.emoji}
              </div>

              {/* Left gold accent line + title */}
              <div className="pl-3 border-l-2 border-gold/40 group-hover:border-gold transition-colors duration-300 mb-3" data-id="element-259">
                <h3 className="text-white font-bold text-sm md:text-base leading-snug" data-id="element-260">
                  {pain.title}
                </h3>
              </div>

              <p className="text-white/45 text-xs md:text-sm leading-relaxed" data-id="element-261">
                {pain.text}
              </p>
            </motion.div>)}
        </div>

        {/* ── BOTTOM HOOK ── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.55,
        delay: 0.3
      }} className="mt-12 text-center" data-id="element-262">
          <div className="inline-block bg-gold/[0.07] border border-gold/20 rounded-2xl px-6 py-5 max-w-2xl" data-id="element-263">
            <p className="text-white/80 text-base md:text-lg leading-relaxed" data-id="element-264">
              Si te identificaste con al menos <span className="text-gold font-bold" data-id="element-265">una sola</span> de estas situaciones, el Kit Blindaje Total Argentina fue hecho exactamente para vos.
            </p>
          </div>
        </motion.div>

      </div>
    </section>;
}